import { useState, useMemo, useEffect } from 'react'
import { useImporterViewModel } from '../ImporterViewModel'
import { ClientDataTable } from 'components/ClientDataTable/ClientDataTable'
import { ColumnDef } from '@tanstack/react-table'
import { FbLink } from 'components/FbUI/FbLink'
import FbButton from 'components/FbUI/FbButton'
import { oppTypesMap } from 'utils/oppTypesMap'
import { ProcessType } from './types'

type ImportTab = 'ugcs' | 'tags' | 'deals' | 'contacts' | 'notes'

interface DetailedImportViewProps {
  show: boolean
  viewModel: ReturnType<typeof useImporterViewModel>
  onImportTags: () => void
  onImportDeals: () => void
  onImportContacts: () => void
  onImportNotes: () => void
  isProcessing: boolean
  currentProcess: ProcessType | null
}

/** Config for each tab: label, whether it has an import button, and the process type */
const TAB_CONFIG: Record<
  ImportTab,
  {
    label: string
    importLabel?: string
    processType?: ProcessType
  }
> = {
  ugcs: { label: 'UGCs' },
  tags: {
    label: 'Tags',
    importLabel: 'Import Tags',
    processType: ProcessType.SETTING_TAGS,
  },
  deals: {
    label: 'Deals',
    importLabel: 'Import Deals',
    processType: ProcessType.SETTING_DEALS,
  },
  contacts: {
    label: 'Contacts',
    importLabel: 'Import Contacts',
    processType: ProcessType.SETTING_CONTACTS,
  },
  notes: {
    label: 'Notes',
    importLabel: 'Import Notes',
    processType: ProcessType.SETTING_COMPANY_NOTES,
  },
}

export function DetailedImportView({
  show,
  viewModel,
  onImportTags,
  onImportDeals,
  onImportContacts,
  onImportNotes,
  isProcessing,
  currentProcess,
}: DetailedImportViewProps) {
  const c = viewModel.importCounts

  // Only show tabs that have data (total > 0)
  const visibleTabs = useMemo(() => {
    const allTabs: ImportTab[] = ['ugcs', 'tags', 'deals', 'contacts', 'notes']
    return allTabs.filter((key) => c[key].total > 0)
  }, [c])

  const [activeTab, setActiveTab] = useState<ImportTab | null>(null)

  // Set initial tab to first visible tab, and reset if current tab becomes hidden.
  // Uses functional setState to avoid activeTab in the dependency array.
  useEffect(() => {
    if (visibleTabs.length === 0) {
      setActiveTab(null)
      return
    }
    setActiveTab((prev) => {
      if (!prev || !visibleTabs.includes(prev)) {
        return visibleTabs[0]
      }
      return prev
    })
  }, [visibleTabs])

  // Import handler map
  const importHandlers: Partial<Record<ImportTab, () => void>> = useMemo(
    () => ({
      tags: onImportTags,
      deals: onImportDeals,
      contacts: onImportContacts,
      notes: onImportNotes,
    }),
    [onImportTags, onImportDeals, onImportContacts, onImportNotes]
  )

  // Status column shared across all tables
  const statusColumn: ColumnDef<any> = {
    header: 'Status',
    accessorKey: 'isImported',
    cell: ({ row }) => {
      const isImported = row.original.isImported
      return (
        <span
          className={`px-2 py-1 text-xs font-medium rounded ${
            isImported
              ? 'bg-green-100 text-green-700'
              : 'bg-gray-100 text-gray-600'
          }`}
        >
          {isImported ? 'Imported' : 'Pending'}
        </span>
      )
    },
  }

  // Company column shared across all tables
  const companyColumn: ColumnDef<any> = {
    header: 'Company',
    accessorKey: 'companyName',
    cell: ({ row }) => {
      const { companyName, isCustomCompany, firstbiteId } = row.original

      if (!firstbiteId) {
        return (
          <div className="font-semibold text-gray-900">
            {companyName}
            {isCustomCompany && (
              <span className="ml-2 text-xs text-gray-500">(UGC)</span>
            )}
          </div>
        )
      }

      const oppType = firstbiteId.substring(0, 2) as keyof typeof oppTypesMap
      const chainPageUrl = oppTypesMap[oppType]?.chainPageUrl ?? '/company/'
      const url = `${chainPageUrl}${firstbiteId}`

      return (
        <div className="font-semibold">
          <FbLink to={url}>{companyName}</FbLink>
          {isCustomCompany && (
            <span className="ml-2 text-xs text-gray-500">(UGC)</span>
          )}
        </div>
      )
    },
  }

  // Define columns for each tab
  const ugcsColumns: ColumnDef<any>[] = [statusColumn, companyColumn]

  const tagsColumns: ColumnDef<any>[] = [
    statusColumn,
    companyColumn,
    {
      header: 'Tag Name',
      accessorKey: 'tag_name',
      cell: ({ row }) => (
        <span className="px-2 py-1 bg-new-company-pistachio-light text-fb-dark-green text-xs rounded">
          {row.original.tag_name}
        </span>
      ),
    },
  ]

  const dealsColumns: ColumnDef<any>[] = [
    statusColumn,
    companyColumn,
    {
      header: 'Sales Stage',
      accessorKey: 'deal_sales_stage',
      cell: ({ row }) => row.original.deal_sales_stage || '-',
    },
    {
      header: 'Account Owner',
      accessorKey: 'deal_account_owner',
      cell: ({ row }) => row.original.deal_account_owner || '-',
    },
    {
      header: 'Product',
      accessorKey: 'deal_product',
      cell: ({ row }) => row.original.deal_product || '-',
    },
  ]

  const contactsColumns: ColumnDef<any>[] = [
    statusColumn,
    companyColumn,
    {
      header: 'Name',
      accessorKey: 'contact_full_name',
      cell: ({ row }) => {
        const fullName =
          row.original.contact_full_name ||
          `${row.original.contact_first_name || ''} ${row.original.contact_last_name || ''}`.trim()
        return fullName || '-'
      },
    },
    {
      header: 'Title',
      accessorKey: 'contact_title',
      cell: ({ row }) => row.original.contact_title || '-',
    },
    {
      header: 'Email',
      accessorKey: 'contact_work_email',
      cell: ({ row }) => {
        const email =
          row.original.contact_work_email ||
          row.original.contact_personal_email ||
          row.original.contact_generic_email
        return email || '-'
      },
    },
    {
      header: 'Phone',
      accessorKey: 'contact_phone',
      cell: ({ row }) => row.original.contact_phone || '-',
    },
    {
      header: 'Skip Reason',
      accessorKey: 'failed_import_reason',
      cell: ({ row }) => {
        const reason = row.original.failed_import_reason
        if (!reason) return '-'
        return (
          <span className="px-2 py-1 text-xs font-medium rounded bg-amber-100 text-amber-700">
            {reason}
          </span>
        )
      },
    },
  ]

  const notesColumns: ColumnDef<any>[] = [
    statusColumn,
    companyColumn,
    {
      header: 'Note',
      accessorKey: 'company_note',
      cell: ({ row }) => (
        <span className="text-gray-700">{row.original.company_note}</span>
      ),
    },
  ]

  // Get data and columns for active tab
  const { data, columns } = useMemo(() => {
    switch (activeTab) {
      case 'ugcs':
        return { data: viewModel.detailedImportData.ugcs, columns: ugcsColumns }
      case 'tags':
        return { data: viewModel.detailedImportData.tags, columns: tagsColumns }
      case 'deals':
        return {
          data: viewModel.detailedImportData.deals,
          columns: dealsColumns,
        }
      case 'contacts':
        return {
          data: viewModel.detailedImportData.contacts,
          columns: contactsColumns,
        }
      case 'notes':
        return {
          data: viewModel.detailedImportData.notes,
          columns: notesColumns,
        }
      default:
        return { data: [], columns: [] as ColumnDef<any>[] }
    }
  }, [activeTab, viewModel.detailedImportData])

  if (!show) {
    return null
  }

  if (visibleTabs.length === 0 || !activeTab) {
    return (
      <div className="text-center text-gray-500 py-12">No data to import.</div>
    )
  }

  const activeConfig = TAB_CONFIG[activeTab]
  const activeCounts = c[activeTab]
  const activeImportHandler = importHandlers[activeTab]

  return (
    <>
      {/* Tab bar */}
      <div className="border-b border-gray-200 mb-4">
        <div className="flex gap-2">
          {visibleTabs.map((tabKey) => {
            const config = TAB_CONFIG[tabKey]
            const counts = c[tabKey]
            const isActive = activeTab === tabKey

            return (
              <button
                key={tabKey}
                onClick={() => setActiveTab(tabKey)}
                className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                  isActive
                    ? 'border-fb-dark-green text-fb-dark-green'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {config.label}{' '}
                <span
                  className={
                    counts.imported === counts.total
                      ? 'text-green-600'
                      : 'opacity-60'
                  }
                >
                  {counts.imported}/{counts.total}
                </span>
              </button>
            )
          })}
        </div>
      </div>

      {/* Import button (for importable tabs) */}
      {activeConfig.importLabel && activeImportHandler && (
        <div className="flex justify-end mb-3">
          <FbButton
            size="sm"
            variant="primary"
            onClick={() => {
              activeImportHandler()
            }}
            loading={
              isProcessing && currentProcess === activeConfig.processType
            }
            disabled={
              activeCounts.imported === activeCounts.total ||
              activeCounts.total === 0
            }
          >
            {activeConfig.importLabel}
          </FbButton>
        </div>
      )}

      <ClientDataTable
        height={'100%'}
        data={data}
        columns={columns}
        enableSorting
      />
    </>
  )
}
